import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ChartBarIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  PhotoIcon,
  LinkIcon,
  GlobeAltIcon,
  ClockIcon,
  DevicePhoneMobileIcon,
} from "@heroicons/react/24/outline";

/**
 * SEO Audit Component - Comprehensive SEO health checker
 */
const SEOAudit = ({
  showAudit = false,
  onToggle = () => {},
  autoRun = true,
  detailed = true,
}) => {
  const location = useLocation();
  const [auditResults, setAuditResults] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [score, setScore] = useState(0);
  const [lastAudit, setLastAudit] = useState(null);

  // Run audit when location changes or component mounts
  useEffect(() => {
    if (autoRun && showAudit) {
      runAudit();
    }
  }, [location.pathname, showAudit, autoRun]);

  // Main audit function
  const runAudit = async () => {
    setIsRunning(true);
    const startTime = Date.now();

    try {
      const results = await performSEOAudit();
      setAuditResults(results);
      setScore(calculateOverallScore(results));
      setLastAudit(new Date().toISOString());
    } catch (error) {
      console.error("SEO Audit failed:", error);
    } finally {
      setIsRunning(false);
    }
  };

  // Perform comprehensive SEO audit
  const performSEOAudit = async () => {
    const checks = {
      // Meta Tags
      title: checkTitle(),
      description: checkDescription(),
      keywords: checkKeywords(),
      viewport: checkViewport(),
      charset: checkCharset(),

      // Open Graph
      ogTitle: checkOGTitle(),
      ogDescription: checkOGDescription(),
      ogImage: checkOGImage(),
      ogType: checkOGType(),
      ogUrl: checkOGUrl(),

      // Twitter Cards
      twitterCard: checkTwitterCard(),
      twitterTitle: checkTwitterTitle(),
      twitterDescription: checkTwitterDescription(),
      twitterImage: checkTwitterImage(),

      // Technical SEO
      canonical: checkCanonical(),
      robots: checkRobots(),
      lang: checkLang(),
      favicon: checkFavicon(),
      manifest: checkManifest(),

      // Content Structure
      headings: checkHeadings(),
      images: checkImages(),
      links: checkLinks(),
      structuredData: checkStructuredData(),

      // Performance
      performance: await checkPerformance(),

      // Accessibility
      accessibility: checkAccessibility(),

      // Mobile
      mobile: checkMobile(),

      // Security
      security: checkSecurity(),
    };

    return checks;
  };

  // Individual check functions
  const checkTitle = () => {
    const title = document.title;
    const issues = [];

    if (!title) {
      return {
        status: "error",
        message: "Missing title tag",
        issues: ["No title found"],
      };
    }

    if (title.length < 30) issues.push("Title too short (< 30 characters)");
    if (title.length > 60) issues.push("Title too long (> 60 characters)");
    if (!title.includes("|") && !title.includes("-"))
      issues.push("Consider adding brand separator");

    return {
      status:
        issues.length === 0
          ? "success"
          : issues.length <= 1
            ? "warning"
            : "error",
      message: `Title: "${title}" (${title.length} chars)`,
      issues,
      value: title,
    };
  };

  const checkDescription = () => {
    const meta = document.querySelector('meta[name="description"]');
    const description = meta?.content;
    const issues = [];

    if (!description) {
      return {
        status: "error",
        message: "Missing meta description",
        issues: ["No description found"],
      };
    }

    if (description.length < 120)
      issues.push("Description too short (< 120 characters)");
    if (description.length > 160)
      issues.push("Description too long (> 160 characters)");
    if (description === document.title)
      issues.push("Description same as title");

    return {
      status:
        issues.length === 0
          ? "success"
          : issues.length <= 1
            ? "warning"
            : "error",
      message: `Description: ${description.length} characters`,
      issues,
      value: description,
    };
  };

  const checkKeywords = () => {
    const meta = document.querySelector('meta[name="keywords"]');
    const keywords = meta?.content;

    if (!keywords) {
      return {
        status: "warning",
        message: "No keywords meta tag",
        issues: ["Consider adding keywords"],
      };
    }

    const keywordList = keywords
      .split(",")
      .map((k) => k.trim())
      .filter((k) => k);
    const issues = [];

    if (keywordList.length < 5) issues.push("Consider adding more keywords");
    if (keywordList.length > 15) issues.push("Too many keywords (> 15)");

    return {
      status: issues.length === 0 ? "success" : "warning",
      message: `Keywords: ${keywordList.length} found`,
      issues,
      value: keywordList,
    };
  };

  const checkViewport = () => {
    const viewport = document.querySelector('meta[name="viewport"]');

    if (!viewport) {
      return {
        status: "error",
        message: "Missing viewport meta tag",
        issues: ["Viewport not found"],
      };
    }

    const content = viewport.content;
    const issues = [];

    if (!content.includes("width=device-width"))
      issues.push("Missing width=device-width");
    if (!content.includes("initial-scale=1"))
      issues.push("Missing initial-scale=1");

    return {
      status: issues.length === 0 ? "success" : "warning",
      message: "Viewport meta tag found",
      issues,
      value: content,
    };
  };

  const checkCharset = () => {
    const charset = document.querySelector("meta[charset]");

    if (!charset) {
      return {
        status: "error",
        message: "Missing charset declaration",
        issues: ["Charset not found"],
      };
    }

    return {
      status: "success",
      message: `Charset: ${charset.getAttribute("charset")}`,
      issues: [],
      value: charset.getAttribute("charset"),
    };
  };

  const checkOGTitle = () => {
    const ogTitle = document.querySelector('meta[property="og:title"]');

    if (!ogTitle) {
      return {
        status: "warning",
        message: "Missing Open Graph title",
        issues: ["OG title not found"],
      };
    }

    return {
      status: "success",
      message: "Open Graph title present",
      issues: [],
      value: ogTitle.content,
    };
  };

  const checkOGDescription = () => {
    const ogDescription = document.querySelector(
      'meta[property="og:description"]',
    );

    if (!ogDescription) {
      return {
        status: "warning",
        message: "Missing Open Graph description",
        issues: ["OG description not found"],
      };
    }

    return {
      status: "success",
      message: "Open Graph description present",
      issues: [],
      value: ogDescription.content,
    };
  };

  const checkOGImage = () => {
    const ogImage = document.querySelector('meta[property="og:image"]');

    if (!ogImage) {
      return {
        status: "warning",
        message: "Missing Open Graph image",
        issues: ["OG image not found"],
      };
    }

    const issues = [];
    const imageUrl = ogImage.content;

    if (!imageUrl.startsWith("https://")) issues.push("Image should use HTTPS");
    if (!imageUrl.includes("1200x630"))
      issues.push("Consider 1200x630px image size");

    return {
      status: issues.length === 0 ? "success" : "warning",
      message: "Open Graph image present",
      issues,
      value: imageUrl,
    };
  };

  const checkOGType = () => {
    const ogType = document.querySelector('meta[property="og:type"]');

    if (!ogType) {
      return {
        status: "warning",
        message: "Missing Open Graph type",
        issues: ["OG type not found"],
      };
    }

    return {
      status: "success",
      message: `Open Graph type: ${ogType.content}`,
      issues: [],
      value: ogType.content,
    };
  };

  const checkOGUrl = () => {
    const ogUrl = document.querySelector('meta[property="og:url"]');

    if (!ogUrl) {
      return {
        status: "warning",
        message: "Missing Open Graph URL",
        issues: ["OG URL not found"],
      };
    }

    const issues = [];

    if (!ogUrl.content.startsWith("https://"))
      issues.push("URL should use HTTPS");
    if (ogUrl.content !== window.location.href)
      issues.push("URL doesn't match current page");

    return {
      status: issues.length === 0 ? "success" : "warning",
      message: "Open Graph URL present",
      issues,
      value: ogUrl.content,
    };
  };

  const checkTwitterCard = () => {
    const twitterCard = document.querySelector('meta[name="twitter:card"]');

    if (!twitterCard) {
      return {
        status: "warning",
        message: "Missing Twitter Card",
        issues: ["Twitter card not found"],
      };
    }

    return {
      status: "success",
      message: `Twitter Card: ${twitterCard.content}`,
      issues: [],
      value: twitterCard.content,
    };
  };

  const checkTwitterTitle = () => {
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');

    return {
      status: twitterTitle ? "success" : "warning",
      message: twitterTitle ? "Twitter title present" : "Missing Twitter title",
      issues: twitterTitle ? [] : ["Twitter title not found"],
      value: twitterTitle?.content,
    };
  };

  const checkTwitterDescription = () => {
    const twitterDescription = document.querySelector(
      'meta[name="twitter:description"]',
    );

    return {
      status: twitterDescription ? "success" : "warning",
      message: twitterDescription
        ? "Twitter description present"
        : "Missing Twitter description",
      issues: twitterDescription ? [] : ["Twitter description not found"],
      value: twitterDescription?.content,
    };
  };

  const checkTwitterImage = () => {
    const twitterImage = document.querySelector('meta[name="twitter:image"]');

    return {
      status: twitterImage ? "success" : "warning",
      message: twitterImage ? "Twitter image present" : "Missing Twitter image",
      issues: twitterImage ? [] : ["Twitter image not found"],
      value: twitterImage?.content,
    };
  };

  const checkCanonical = () => {
    const canonical = document.querySelector('link[rel="canonical"]');

    if (!canonical) {
      return {
        status: "warning",
        message: "Missing canonical URL",
        issues: ["Canonical URL not found"],
      };
    }

    const issues = [];

    if (!canonical.href.startsWith("https://"))
      issues.push("Canonical should use HTTPS");

    return {
      status: issues.length === 0 ? "success" : "warning",
      message: "Canonical URL present",
      issues,
      value: canonical.href,
    };
  };

  const checkRobots = () => {
    const robots = document.querySelector('meta[name="robots"]');

    if (!robots) {
      return {
        status: "info",
        message: "No robots meta tag (default indexing)",
        issues: [],
      };
    }

    const content = robots.content.toLowerCase();
    const issues = [];

    if (content.includes("noindex")) issues.push("Page set to noindex");
    if (content.includes("nofollow")) issues.push("Page set to nofollow");

    return {
      status: issues.length === 0 ? "success" : "warning",
      message: `Robots: ${robots.content}`,
      issues,
      value: robots.content,
    };
  };

  const checkLang = () => {
    const htmlLang = document.documentElement.lang;

    if (!htmlLang) {
      return {
        status: "warning",
        message: "Missing lang attribute",
        issues: ["HTML lang not found"],
      };
    }

    return {
      status: "success",
      message: `Language: ${htmlLang}`,
      issues: [],
      value: htmlLang,
    };
  };

  const checkFavicon = () => {
    const favicon = document.querySelector('link[rel*="icon"]');

    if (!favicon) {
      return {
        status: "warning",
        message: "Missing favicon",
        issues: ["Favicon not found"],
      };
    }

    return {
      status: "success",
      message: "Favicon present",
      issues: [],
      value: favicon.href,
    };
  };

  const checkManifest = () => {
    const manifest = document.querySelector('link[rel="manifest"]');

    return {
      status: manifest ? "success" : "info",
      message: manifest ? "Web manifest present" : "No web manifest",
      issues: manifest ? [] : ["Consider adding web manifest for PWA"],
      value: manifest?.href,
    };
  };

  const checkHeadings = () => {
    const h1s = document.querySelectorAll("h1");
    const h2s = document.querySelectorAll("h2");
    const issues = [];

    if (h1s.length === 0) issues.push("No H1 heading found");
    if (h1s.length > 1) issues.push("Multiple H1 headings found");
    if (h2s.length === 0) issues.push("No H2 headings found");

    const headingStructure = {
      h1: h1s.length,
      h2: h2s.length,
      h3: document.querySelectorAll("h3").length,
      h4: document.querySelectorAll("h4").length,
      h5: document.querySelectorAll("h5").length,
      h6: document.querySelectorAll("h6").length,
    };

    return {
      status:
        issues.length === 0
          ? "success"
          : issues.length <= 1
            ? "warning"
            : "error",
      message: `Headings: ${Object.values(headingStructure).reduce((a, b) => a + b, 0)} total`,
      issues,
      value: headingStructure,
    };
  };

  const checkImages = () => {
    const images = document.querySelectorAll("img");
    const issues = [];
    let imagesWithoutAlt = 0;
    let imagesWithEmptyAlt = 0;

    images.forEach((img) => {
      if (!img.alt) imagesWithoutAlt++;
      if (img.alt === "") imagesWithEmptyAlt++;
    });

    if (imagesWithoutAlt > 0)
      issues.push(`${imagesWithoutAlt} images missing alt text`);
    if (imagesWithEmptyAlt > images.length / 2)
      issues.push("Many images have empty alt text");

    return {
      status: issues.length === 0 ? "success" : "warning",
      message: `Images: ${images.length} total`,
      issues,
      value: {
        total: images.length,
        withoutAlt: imagesWithoutAlt,
        withEmptyAlt: imagesWithEmptyAlt,
      },
    };
  };

  const checkLinks = () => {
    const internalLinks = document.querySelectorAll(
      'a[href^="/"], a[href^="#"]',
    );
    const externalLinks = document.querySelectorAll(
      'a[href^="http"]:not([href*="' + window.location.hostname + '"])',
    );
    const issues = [];

    let externalLinksWithoutTarget = 0;
    externalLinks.forEach((link) => {
      if (!link.target || link.target !== "_blank")
        externalLinksWithoutTarget++;
    });

    if (externalLinksWithoutTarget > 0) {
      issues.push(
        `${externalLinksWithoutTarget} external links without target="_blank"`,
      );
    }

    return {
      status: issues.length === 0 ? "success" : "info",
      message: `Links: ${internalLinks.length} internal, ${externalLinks.length} external`,
      issues,
      value: {
        internal: internalLinks.length,
        external: externalLinks.length,
      },
    };
  };

  const checkStructuredData = () => {
    const jsonLdScripts = document.querySelectorAll(
      'script[type="application/ld+json"]',
    );
    const issues = [];

    if (jsonLdScripts.length === 0) {
      issues.push("No structured data found");
    }

    let validSchemas = 0;
    jsonLdScripts.forEach((script) => {
      try {
        const data = JSON.parse(script.textContent);
        if (data["@context"] && data["@type"]) validSchemas++;
      } catch (e) {
        issues.push("Invalid JSON-LD syntax found");
      }
    });

    return {
      status:
        validSchemas > 0
          ? "success"
          : issues.length === 0
            ? "warning"
            : "error",
      message: `Structured data: ${validSchemas} valid schemas`,
      issues,
      value: {
        total: jsonLdScripts.length,
        valid: validSchemas,
      },
    };
  };

  const checkPerformance = async () => {
    if (!window.performance) {
      return {
        status: "warning",
        message: "Performance API not available",
        issues: [],
      };
    }

    const navigation = performance.getEntriesByType("navigation")[0];
    const paint = performance.getEntriesByType("paint");
    const issues = [];

    if (navigation) {
      const loadTime = navigation.loadEventEnd - navigation.loadEventStart;
      if (loadTime > 3000) issues.push("Page load time > 3s");

      const domContentLoaded =
        navigation.domContentLoadedEventEnd -
        navigation.domContentLoadedEventStart;
      if (domContentLoaded > 1500) issues.push("DOM content loaded > 1.5s");
    }

    const fcp = paint.find((p) => p.name === "first-contentful-paint");
    if (fcp && fcp.startTime > 1800) {
      issues.push("First Contentful Paint > 1.8s");
    }

    return {
      status:
        issues.length === 0
          ? "success"
          : issues.length <= 2
            ? "warning"
            : "error",
      message: `Performance: ${issues.length === 0 ? "Good" : "Needs improvement"}`,
      issues,
      value: {
        loadTime: navigation
          ? Math.round(navigation.loadEventEnd - navigation.loadEventStart)
          : null,
        fcp: fcp ? Math.round(fcp.startTime) : null,
      },
    };
  };

  const checkAccessibility = () => {
    const issues = [];

    // Check for skip links
    const skipLinks = document.querySelectorAll('a[href^="#"]:first-child');
    if (skipLinks.length === 0) issues.push("No skip navigation link found");

    // Check for ARIA landmarks
    const landmarks = document.querySelectorAll(
      '[role="main"], [role="navigation"], [role="banner"], [role="contentinfo"], main, nav, header, footer',
    );
    if (landmarks.length < 3) issues.push("Few ARIA landmarks found");

    // Check for form labels
    const inputs = document.querySelectorAll("input, select, textarea");
    let inputsWithoutLabels = 0;
    inputs.forEach((input) => {
      const label = document.querySelector(`label[for="${input.id}"]`);
      if (!label && !input.getAttribute("aria-label")) inputsWithoutLabels++;
    });

    if (inputsWithoutLabels > 0)
      issues.push(`${inputsWithoutLabels} form inputs without labels`);

    return {
      status:
        issues.length === 0
          ? "success"
          : issues.length <= 2
            ? "warning"
            : "error",
      message: `Accessibility: ${issues.length} issues found`,
      issues,
      value: {
        skipLinks: skipLinks.length,
        landmarks: landmarks.length,
        unlabeledInputs: inputsWithoutLabels,
      },
    };
  };

  const checkMobile = () => {
    const viewport = document.querySelector('meta[name="viewport"]');
    const issues = [];

    if (!viewport) issues.push("No viewport meta tag");

    // Check for mobile-friendly content
    const smallText = document.querySelectorAll("*").length;
    // This is a simplified check - in reality you'd check computed styles

    return {
      status: issues.length === 0 ? "success" : "warning",
      message: `Mobile: ${viewport ? "Viewport configured" : "Not mobile optimized"}`,
      issues,
      value: {
        hasViewport: !!viewport,
        viewportContent: viewport?.content,
      },
    };
  };

  const checkSecurity = () => {
    const issues = [];
    const isHTTPS = window.location.protocol === "https:";

    if (!isHTTPS) issues.push("Site not using HTTPS");

    // Check for mixed content
    const httpResources = document.querySelectorAll(
      '[src^="http:"], [href^="http:"]',
    );
    if (httpResources.length > 0 && isHTTPS) {
      issues.push(`${httpResources.length} mixed content resources found`);
    }

    return {
      status: issues.length === 0 ? "success" : "warning",
      message: `Security: ${isHTTPS ? "HTTPS enabled" : "Not secure"}`,
      issues,
      value: {
        isHTTPS,
        mixedContent: httpResources.length,
      },
    };
  };

  // Calculate overall score
  const calculateOverallScore = (results) => {
    let totalScore = 0;
    let totalChecks = 0;

    Object.values(results).forEach((result) => {
      totalChecks++;
      if (result.status === "success") totalScore += 100;
      else if (result.status === "warning") totalScore += 75;
      else if (result.status === "info") totalScore += 90;
      else totalScore += 25;
    });

    return Math.round(totalScore / totalChecks);
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case "success":
        return <CheckCircleIcon className="w-5 h-5 text-green-600" />;
      case "warning":
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />;
      case "error":
        return <XCircleIcon className="w-5 h-5 text-red-600" />;
      case "info":
        return <InformationCircleIcon className="w-5 h-5 text-blue-600" />;
      default:
        return <InformationCircleIcon className="w-5 h-5 text-gray-600" />;
    }
  };

  // Get score color
  const getScoreColor = (score) => {
    if (score >= 90) return "text-green-600";
    if (score >= 80) return "text-yellow-600";
    if (score >= 70) return "text-orange-600";
    return "text-red-600";
  };

  // Get score grade
  const getScoreGrade = (score) => {
    if (score >= 90) return "A";
    if (score >= 80) return "B";
    if (score >= 70) return "C";
    if (score >= 60) return "D";
    return "F";
  };

  // Group results by category
  const groupResults = (results) => {
    return {
      "Meta Tags": {
        title: results.title,
        description: results.description,
        keywords: results.keywords,
        viewport: results.viewport,
        charset: results.charset,
      },
      "Social Media": {
        ogTitle: results.ogTitle,
        ogDescription: results.ogDescription,
        ogImage: results.ogImage,
        ogType: results.ogType,
        ogUrl: results.ogUrl,
        twitterCard: results.twitterCard,
        twitterTitle: results.twitterTitle,
        twitterDescription: results.twitterDescription,
        twitterImage: results.twitterImage,
      },
      "Technical SEO": {
        canonical: results.canonical,
        robots: results.robots,
        lang: results.lang,
        favicon: results.favicon,
        manifest: results.manifest,
        structuredData: results.structuredData,
      },
      "Content & Structure": {
        headings: results.headings,
        images: results.images,
        links: results.links,
      },
      "Performance & UX": {
        performance: results.performance,
        mobile: results.mobile,
        accessibility: results.accessibility,
        security: results.security,
      },
    };
  };

  if (!showAudit) return null;

  return (
    <>
      <Helmet>
        <style>
          {`
            .seo-audit-panel {
              position: fixed;
              top: 0;
              right: 0;
              width: 400px;
              height: 100vh;
              background: white;
              border-left: 1px solid #e5e7eb;
              z-index: 10000;
              overflow-y: auto;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
              font-size: 14px;
              box-shadow: -4px 0 8px rgba(0, 0, 0, 0.1);
            }

            .seo-audit-overlay {
              position: fixed;
              top: 0;
              left: 0;
              width: 100vw;
              height: 100vh;
              background: rgba(0, 0, 0, 0.3);
              z-index: 9999;
            }
          `}
        </style>
      </Helmet>

      <div className="seo-audit-overlay" onClick={onToggle} />

      <div className="seo-audit-panel">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <MagnifyingGlassIcon className="w-6 h-6 text-blue-600" />
              <h2 className="text-lg font-semibold">SEO Audit</h2>
            </div>
            <button
              onClick={onToggle}
              className="text-gray-500 hover:text-gray-700"
            >
              <XCircleIcon className="w-6 h-6" />
            </button>
          </div>

          {auditResults && (
            <div className="mt-4">
              <div className="flex items-center justify-between">
                <div className="text-2xl font-bold">
                  <span className={getScoreColor(score)}>{score}/100</span>
                  <span className="text-sm text-gray-500 ml-2">
                    Grade {getScoreGrade(score)}
                  </span>
                </div>
                <button
                  onClick={runAudit}
                  disabled={isRunning}
                  className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  {isRunning ? "Running..." : "Re-run Audit"}
                </button>
              </div>

              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div
                  className={`h-2 rounded-full transition-all duration-500 ${
                    score >= 90
                      ? "bg-green-600"
                      : score >= 80
                        ? "bg-yellow-500"
                        : score >= 70
                          ? "bg-orange-500"
                          : "bg-red-500"
                  }`}
                  style={{ width: `${score}%` }}
                />
              </div>

              {lastAudit && (
                <p className="text-xs text-gray-500 mt-2">
                  Last audit: {new Date(lastAudit).toLocaleTimeString()}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="p-4">
          {!auditResults && !isRunning && (
            <div className="text-center py-8">
              <ChartBarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">
                Run an SEO audit to check this page's optimization
              </p>
              <button
                onClick={runAudit}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Start Audit
              </button>
            </div>
          )}

          {isRunning && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Running SEO audit...</p>
            </div>
          )}

          {auditResults && (
            <div className="space-y-6">
              {Object.entries(groupResults(auditResults)).map(
                ([category, checks]) => (
                  <div key={category} className="space-y-3">
                    <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
                      {category === "Meta Tags" && (
                        <DocumentTextIcon className="w-4 h-4" />
                      )}
                      {category === "Social Media" && (
                        <GlobeAltIcon className="w-4 h-4" />
                      )}
                      {category === "Technical SEO" && (
                        <MagnifyingGlassIcon className="w-4 h-4" />
                      )}
                      {category === "Content & Structure" && (
                        <DocumentTextIcon className="w-4 h-4" />
                      )}
                      {category === "Performance & UX" && (
                        <ChartBarIcon className="w-4 h-4" />
                      )}
                      <span>{category}</span>
                    </h3>

                    <div className="space-y-2">
                      {Object.entries(checks).map(([checkName, result]) => (
                        <div
                          key={checkName}
                          className="bg-gray-50 rounded-lg p-3"
                        >
                          <div className="flex items-start space-x-2">
                            {getStatusIcon(result.status)}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900">
                                {checkName
                                  .replace(/([A-Z])/g, " $1")
                                  .replace(/^./, (str) => str.toUpperCase())}
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                {result.message}
                              </div>
                              {result.issues.length > 0 && (
                                <ul className="text-xs text-red-600 mt-1 space-y-1">
                                  {result.issues.map((issue, idx) => (
                                    <li key={idx}>• {issue}</li>
                                  ))}
                                </ul>
                              )}
                              {detailed && result.value && (
                                <div className="text-xs text-gray-500 mt-2 font-mono bg-white p-2 rounded border">
                                  {typeof result.value === "object"
                                    ? JSON.stringify(result.value, null, 2)
                                    : result.value}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ),
              )}

              {/* Summary Section */}
              <div className="border-t pt-4 mt-6">
                <h3 className="font-semibold text-gray-900 mb-3">Summary</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {
                        Object.values(auditResults).filter(
                          (r) => r.status === "success",
                        ).length
                      }
                    </div>
                    <div className="text-green-700">Passed</div>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600">
                      {
                        Object.values(auditResults).filter(
                          (r) => r.status === "warning",
                        ).length
                      }
                    </div>
                    <div className="text-yellow-700">Warnings</div>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">
                      {
                        Object.values(auditResults).filter(
                          (r) => r.status === "error",
                        ).length
                      }
                    </div>
                    <div className="text-red-700">Errors</div>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {
                        Object.values(auditResults).filter(
                          (r) => r.status === "info",
                        ).length
                      }
                    </div>
                    <div className="text-blue-700">Info</div>
                  </div>
                </div>
              </div>

              {/* Recommendations */}
              <div className="border-t pt-4 mt-6">
                <h3 className="font-semibold text-gray-900 mb-3">
                  Recommendations
                </h3>
                <div className="space-y-2 text-sm">
                  {Object.values(auditResults)
                    .filter((r) => r.issues.length > 0)
                    .slice(0, 5)
                    .map((result, idx) => (
                      <div
                        key={idx}
                        className="p-2 bg-blue-50 rounded border-l-4 border-blue-400"
                      >
                        <div className="font-medium text-blue-900">
                          {result.issues[0]}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SEOAudit;

// Export audit utilities
export const runQuickSEOCheck = () => {
  const issues = [];

  if (!document.title || document.title.length < 30) {
    issues.push("Title too short or missing");
  }

  const description = document.querySelector('meta[name="description"]');
  if (!description || description.content.length < 120) {
    issues.push("Meta description too short or missing");
  }

  const h1s = document.querySelectorAll("h1");
  if (h1s.length !== 1) {
    issues.push("Should have exactly one H1 tag");
  }

  return {
    score: Math.max(0, 100 - issues.length * 25),
    issues,
  };
};

export const generateSEOReport = (auditResults) => {
  const report = {
    timestamp: new Date().toISOString(),
    url: window.location.href,
    score: 0,
    categories: {},
    issues: [],
    recommendations: [],
  };

  if (auditResults) {
    let totalScore = 0;
    let totalChecks = 0;

    Object.entries(auditResults).forEach(([key, result]) => {
      totalChecks++;
      if (result.status === "success") totalScore += 100;
      else if (result.status === "warning") totalScore += 75;
      else if (result.status === "info") totalScore += 90;
      else totalScore += 25;

      if (result.issues.length > 0) {
        report.issues.push({
          check: key,
          issues: result.issues,
          severity: result.status,
        });
      }
    });

    report.score = Math.round(totalScore / totalChecks);
  }

  return report;
};
